import { memo, useState } from 'react'

import { DateTime, TableColumn, UsPhoneLabel } from 'ui/components'
import { Link } from 'ui/components/Link'
import { ApplicationPipelineItem } from '~bank-reports/types/ApplicationPipelineReport'

import * as SC from './styles'

const MAX_NOTES_INLINE_LENGTH = 50

const isEmpty = (value: string) => !value || value.trim().length === 0

const NotesCell = memo((props: { value: string }) => {
  const { value } = props
  const [expanded, setExpanded] = useState(false)
  const hasNewLine = value.includes('\n')
  const canExpand = hasNewLine || value.length > MAX_NOTES_INLINE_LENGTH
  const collapsedValue =
    value.length > MAX_NOTES_INLINE_LENGTH ? `${value.slice(0, MAX_NOTES_INLINE_LENGTH)}...` : value
  const shownValue = canExpand && !expanded ? collapsedValue : value

  if (isEmpty(value)) {
    return null
  }

  return (
    <SC.NotesCell>
      <SC.NotesText $expanded={expanded}>{shownValue}</SC.NotesText>
      {canExpand && (
        <SC.NotesToggleLink onClick={() => setExpanded((prev) => !prev)}>
          {expanded ? 'Collapse' : 'Expand'}
        </SC.NotesToggleLink>
      )}
    </SC.NotesCell>
  )
})

export const columns: TableColumn<ApplicationPipelineItem>[] = [
  {
    title: 'ID',
    render: ({ applicationId }) => (
      <SC.IdColumn>
        <Link to="/onboarding/applications/$applicationId" params={{ applicationId: applicationId.toString() }}>
          ID-{applicationId}
        </Link>
      </SC.IdColumn>
    )
  },
  {
    title: 'Company Name',
    key: 'companyName',
    align: 'left',
    width: '300px'
  },
  {
    title: 'Status',
    key: 'status',
    align: 'right'
  },
  {
    title: 'Contact Name',
    key: 'contactName',
    align: 'left'
  },
  {
    title: 'Email',
    align: 'left',
    render: ({ email }) => <SC.EmailLink href={`mailto:${email}`}>{email}</SC.EmailLink>
  },
  {
    title: 'Phone',
    align: 'left',
    render: ({ phone }) => (
      <>
        {phone && (
          <SC.Phone>
            <UsPhoneLabel value={phone || ''} />
          </SC.Phone>
        )}
      </>
    )
  },
  {
    title: 'Current Owner',
    key: 'currentOwner',
    align: 'left'
  },
  {
    title: 'Active',
    align: 'left',
    render: ({ active }) => (active ? 'Yes' : 'No')
  },
  {
    title: 'Internal Notes',
    align: 'left',
    width: '220px',
    render: ({ internalNotes }) => <NotesCell value={internalNotes} />
  },
  {
    title: 'Banker Notes',
    align: 'left',
    width: '220px',
    render: ({ bankerNotes }) => <NotesCell value={bankerNotes} />
  },
  {
    title: 'Client Notes',
    align: 'left',
    width: '220px',
    render: ({ clientNotes }) => <NotesCell value={clientNotes} />
  },
  {
    title: 'Business Type 1',
    key: 'businessType1',
    align: 'left'
  },
  {
    title: 'Business Type 2',
    key: 'businessType2',
    align: 'left'
  },
  {
    title: 'Business Type 3',
    key: 'businessType3',
    align: 'left'
  },
  {
    title: 'Business Type 4',
    key: 'businessType4',
    align: 'left'
  },
  {
    title: 'Created At',
    align: 'left',
    render: ({ createdAt }) => <DateTime withTime date={createdAt} />
  },
  {
    title: 'Last Modified',
    align: 'left',
    render: ({ lastModified }) => <DateTime withTime date={lastModified} />
  },
  {
    title: 'Created By',
    key: 'createdBy',
    align: 'left'
  }
]
